import { useState } from 'react'
import { Modal } from './Modal'
import { EmojiPick } from './EmojiPick'
import { api, ApiError, type BulkRow } from '../lib/api'
import { useToast } from '../lib/toast'
import type { Category } from '@shared/types'

interface Props {
  categories: Category[]
  defaultCategoryId: string
  /** The key is set and this family is approved: the paste comes back sorted. */
  aiAvailable: boolean
  /** Why it is not, when it is not; '' when it is. */
  aiNote?: string
  onAdded: () => void
  onClose: () => void
  onError: (err: unknown, fallback: string) => void
}

/**
 * Paste a list, check it, add it -- or skip the paste and type the rows in.
 *
 * One modal, two ways in. The review screen is the real form: a row per
 * food with an emoji, a name and a category, all editable, nothing written
 * until the rows on screen are the rows you want. With the assistant
 * available the paste arrives sorted and with emoji; without it (no key, or
 * a family still waiting for approval) the same rows arrive in the category
 * you picked and you sort them yourself. Either way the form is the same.
 */
export function BulkAdd({ categories, defaultCategoryId, aiAvailable, aiNote = '', onAdded, onClose, onError }: Props) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [categoryId, setCategoryId] = useState(defaultCategoryId)
  const [rows, setRows] = useState<BulkRow[] | null>(null)
  const [skipped, setSkipped] = useState<Set<number>>(new Set())
  const [usedAi, setUsedAi] = useState(false)
  const [busy, setBusy] = useState(false)

  async function parse() {
    if (busy || !text.trim()) return
    setBusy(true)
    try {
      const res = await api.bulkParse(text, categoryId)
      setRows(res.rows)
      setUsedAi(res.usedAi)
      // Anything already in the pantry starts unticked; adding a second
      // "Pretzels" is almost never what a paste meant.
      setSkipped(new Set(res.rows.flatMap((r, i) => (r.duplicateOf ? [i] : []))))
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'Could not read that list.')
    } finally {
      setBusy(false)
    }
  }

  /** Straight to the form with one empty row: no paste, no model, just typing. */
  function startTyping() {
    setRows([blank()])
    setUsedAi(false)
    setSkipped(new Set())
  }

  const blank = (): BulkRow => ({ name: '', categoryId, emoji: '', duplicateOf: null })

  function addRow() {
    setRows((prev) => [...(prev ?? []), blank()])
  }

  function removeRow(i: number) {
    setRows((prev) => prev!.filter((_, n) => n !== i))
    setSkipped((prev) => new Set([...prev].filter((n) => n !== i).map((n) => (n > i ? n - 1 : n))))
  }

  async function commit() {
    if (busy || !rows) return
    const keep = rows.filter((r, i) => !skipped.has(i) && r.name.trim())
    if (keep.length === 0) return
    setBusy(true)
    try {
      const added = await api.bulkAdd(
        keep.map((r) => ({ name: r.name.trim(), categoryId: r.categoryId, emoji: r.emoji, notes: '' })),
      )
      toast.notify(`Added ${added.length} item${added.length === 1 ? '' : 's'}.`)
      onAdded()
      onClose()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'Could not add those.')
      setBusy(false)
    }
  }

  const patch = (i: number, change: Partial<BulkRow>) =>
    setRows((prev) => prev!.map((r, n) => (n === i ? { ...r, ...change } : r)))

  const keeping = rows ? rows.filter((r, i) => !skipped.has(i) && r.name.trim()).length : 0
  const duplicates = rows?.filter((r) => r.duplicateOf).length ?? 0

  return (
    <Modal
      title={rows ? 'Check these before adding' : 'Bulk add to the pantry'}
      onClose={onClose}
      footer={
        rows ? (
          <>
            <button className="btn ghost" onClick={() => setRows(null)} disabled={busy}>
              Back
            </button>
            <div className="spacer" />
            <button className="btn primary" onClick={commit} disabled={busy || keeping === 0}>
              {busy ? 'Adding…' : `Add ${keeping} item${keeping === 1 ? '' : 's'}`}
            </button>
          </>
        ) : (
          <>
            <button className="btn ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <div className="spacer" />
            <button className="btn primary" onClick={parse} disabled={busy || !text.trim()}>
              {busy ? (aiAvailable ? 'Sorting…' : 'Reading…') : 'Next'}
            </button>
          </>
        )
      }
    >
      {rows === null ? (
        <>
          <div className="field">
            <label htmlFor="bulk-text">One per line, or separated by commas</label>
            <textarea
              id="bulk-text"
              className="note-input"
              style={{ minHeight: 160 }}
              value={text}
              placeholder={'Pretzels\nSeaweed snacks\nFreeze-dried strawberries\nRice cakes'}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="bulk-cat">{aiAvailable ? 'If it cannot tell, put them in' : 'Put them all in'}</label>
            <select id="bulk-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.emoji} {c.name}
                </option>
              ))}
            </select>
            <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
              {aiAvailable
                ? 'Each one gets a suggested category and emoji, which you can change on the next screen. Nothing is added until you confirm.'
                : `These go in the category you pick. You can set an emoji and move any of them on the next screen.${aiNote ? ` ${aiNote}` : ''}`}
            </p>
          </div>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            No list to paste?{' '}
            <button type="button" className="linklike" onClick={startTyping}>
              Type them in one at a time
            </button>
            .
          </p>
        </>
      ) : (
        <>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {rows.length === 0
              ? 'Add a row to start.'
              : `${rows.length} row${rows.length === 1 ? '' : 's'}${usedAi ? ', sorted and given emoji for you' : ''}.`}
            {duplicates > 0 && ` ${duplicates} already in the pantry — unticked, tick one to add it anyway.`}
          </p>
          <div className="bulk-rows">
            {rows.map((row, i) => (
              <div className={`bulk-row ${skipped.has(i) ? 'skipped' : ''}`} key={i}>
                <input
                  type="checkbox"
                  checked={!skipped.has(i)}
                  aria-label={`Add ${row.name || `row ${i + 1}`}`}
                  onChange={() =>
                    setSkipped((prev) => {
                      const next = new Set(prev)
                      if (next.has(i)) next.delete(i)
                      else next.add(i)
                      return next
                    })
                  }
                />
                <EmojiPick value={row.emoji} label={`Emoji for ${row.name || `row ${i + 1}`}`} onChange={(emoji) => patch(i, { emoji })} />
                <input
                  className="bulk-name"
                  type="text"
                  value={row.name}
                  maxLength={80}
                  placeholder="Food"
                  aria-label={`Name of item ${i + 1}`}
                  autoFocus={row.name === '' && i === rows.length - 1}
                  onChange={(e) => patch(i, { name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && i === rows.length - 1 && row.name.trim()) {
                      e.preventDefault()
                      addRow()
                    }
                  }}
                />
                <select
                  value={row.categoryId}
                  aria-label={`Category for ${row.name || `row ${i + 1}`}`}
                  onChange={(e) => patch(i, { categoryId: e.target.value })}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button type="button" className="bulk-x" aria-label={`Remove row ${i + 1}`} onClick={() => removeRow(i)}>
                  ×
                </button>
                {row.duplicateOf && (
                  <span className="bulk-dupe" title={`Already in the pantry as ${row.duplicateOf}`}>
                    already here
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="row">
            <button type="button" className="btn sm" onClick={addRow}>
              + Add a row
            </button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Enter on the last row adds another.
            </span>
          </div>
        </>
      )}
    </Modal>
  )
}
